import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import Button from "@mui/material/Button"
import InputAdornment from "@mui/material/InputAdornment"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TextField from "@mui/material/TextField"
import { capitalize } from "lodash"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column, {
  PaddedContentColumn,
} from "../../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../../common/components/ConditionalLinearProgress"
import PageTopSection from "../../../../common/components/PageTopSection"
import { RowButColumnOnMobile } from "../../../../common/components/Row"
import NoResultsTableRow from "../../../../common/components/tables/NoResultsTableRow"
import ScrollableTableContainer from "../../../../common/components/tables/ScrollableTableContainer"
import TablePaginationRow from "../../../../common/components/tables/TablePaginationRow"
import {
  HeaderTableCell,
  NoWrapTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  StickyTableCell,
} from "../../../../common/components/tables/cells"
import {
  formatTableDate,
  getTableSearchParams,
  usePaginationState,
  useTableSearchTextState,
  useTableSort,
} from "../../../../common/components/tables/utils"
import { trimLongString } from "../../../../common/utils"
import BorderedGrayChip from "../../components/BorderedGrayChip"
import { canEditResource } from "../../utils/resourceAccess"
import CreateWorkflowDialog from "./CreateWorkflowDialog"

export default function Workflows() {
  const [workflows, setWorkflows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateWorkflowDialogOpen, setIsCreateWorkflowDialogOpen] =
    useState(false)
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState()
  const [searchText, setSearchText, debouncedSearchText] =
    useTableSearchTextState("q", setPageIndex)
  const { sortColumn, sortDirection, serverSort, onSortColumnChanged } =
    useTableSort("created", "desc")
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()

  useEffect(
    () => {
      setIsLoading(true)
      axios
        .get(`/workflows/api/workflows/`, {
          params: getTableSearchParams(
            debouncedSearchText,
            pageIndex,
            pageSize,
            serverSort
          ),
        })
        .then(({ data }) => {
          setWorkflows(data.results)
          setTotalResultsSize(data.count)
        })
        .finally(() => setIsLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchText, serverSort, pageIndex, pageSize]
  )

  return (
    <Column>
      <PageTopSection title="Workflows" />
      <ConditionalPurpleLinearProgress visible={isLoading} />
      <PaddedContentColumn gap={3}>
        <RowButColumnOnMobile
          sx={{
            justifyContent: "flex-end",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 2,
          }}
        >
          <TextField
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: "15em" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsCreateWorkflowDialogOpen(true)}
            startIcon={<AddCircleOutlineIcon />}
          >
            Create workflow
          </Button>
          <CreateWorkflowDialog
            open={isCreateWorkflowDialogOpen}
            onClose={() => setIsCreateWorkflowDialogOpen(false)}
          />
        </RowButColumnOnMobile>
        <ScrollableTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell
                  HeaderCellComponent={StickyHeaderTableCell}
                  columnName="name"
                  label="Name"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="status"
                  label="Status"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="description"
                  label="Description"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
                <SortableHeaderCell
                  HeaderCellComponent={HeaderTableCell}
                  columnName="created"
                  label="Created at"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onClick={onSortColumnChanged}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, index) => (
                  <TableRow key={index}>
                    <StickyTableCell>
                      <Skeleton variant="text" width={200} />
                    </StickyTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="rectangular" width={80} height={24} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={300} />
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      <Skeleton variant="text" width={150} />
                    </NoWrapTableCell>
                  </TableRow>
                ))
              ) : workflows.length === 0 ? (
                <NoResultsTableRow colSpan={4} />
              ) : (
                workflows.map((workflow) => (
                  <TableRow
                    key={workflow.id}
                    onClick={() => {
                      if (!canEditResource(workflow)) return
                      navigate(`${workflow.id}/`)
                    }}
                    sx={{
                      cursor: canEditResource(workflow) ? "pointer" : "default",
                      opacity: canEditResource(workflow) ? 1 : 0.72,
                    }}
                  >
                    <StickyTableCell title={workflow.name}>
                      {trimLongString(workflow.name)}
                    </StickyTableCell>
                    <NoWrapTableCell>
                      <BorderedGrayChip label={capitalize(workflow.status)} />
                    </NoWrapTableCell>
                    <NoWrapTableCell title={workflow.description}>
                      {workflow.description
                        ? trimLongString(workflow.description)
                        : ""}
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      {formatTableDate(workflow.created)}
                    </NoWrapTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollableTableContainer>
        <TablePaginationRow
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          tableRows={workflows}
          totalResultsSize={totalResultsSize}
        />
      </PaddedContentColumn>
    </Column>
  )
}
